import React, {useEffect, useRef, useState} from "react"
import {
	AuditLogEvent,
	AuditLogEventsByUserAndTimestamp,
	AuditLogEventsView,
} from "../AuditLogEventsView/AuditLogEventsView"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {
	AuditLog,
	AuditLogResource,
	enrichIntentWithSubResourceId,
	findApplicationAuditLogs,
	findCustomerAuditLogs,
	findProductApplicationAuditLogs,
	findAuditLogs,
	findAuditLogsByRelatedAccount,
} from "../../resources/auditLog"
import {useAccessToken} from "../../services/auth"
import {useInfiniteScroll} from "../../hooks/useInfiniteScroll"
import {InfiniteScroll} from "../InfiniteScroll/InfiniteScroll"
import {Result} from "neverthrow"
import {ErrorDocument, OkDocument} from "../../resources/common"

interface AuditLogEventsProps {
	resourceType: AuditLogResource
	resourceId: string
	limit?: number
	showSubResourcesId: boolean
	relatedAccountId?: string
}

function AuditLogPending() {
	return <AuditLogEventsView loading auditLogEventsByUserAndTimestamp={[]} />
}

export const getProcessedAuditLogResults = (
	data: AuditLog[],
	showSubResourcesId: boolean,
	resourceType: AuditLogResource,
	resourceId: string
) => {
	const auditLogsWithSubResourceIds = enrichIntentWithSubResourceId(data, showSubResourcesId, resourceType)

	const groupedEvents: {[key: string]: AuditLogEventsByUserAndTimestamp} = {}
	auditLogsWithSubResourceIds.forEach((event: AuditLog, index: number) => {
		const key = `${event.attributes.actorUserId}-${event.attributes.actionTimestamp}`
		const fileId = event.attributes.fileAttachments?.[0]?.fileAttachment?.id

		if (!groupedEvents[key]) {
			groupedEvents[key] = {
				...(event.attributes.actorUserName && {actorUserName: event.attributes.actorUserName}),
				actorUserRole: event.attributes.actorUserRole,
				actorUserType: event.attributes.actorUserType,
				actionTimestamp: event.attributes.actionTimestamp,
				events: [],
			}
		}

		const auditLogEvent: AuditLogEvent = {
			intent: event.attributes.intent,
			changes: event.attributes.changes,
			originalIndex: index,
			...(event.id && {id: event.id}),
			...(event.attributes.resourceType && {resourceType: event.attributes.resourceType as AuditLogResource}),
			...(fileId && {fileId: fileId}),
			...(resourceId && {resourceId: resourceId}),
		}
		groupedEvents[key].events.push(auditLogEvent)
	})
	return Object.values(groupedEvents)
}

const LIMIT_DEFAULT = 50

export function AuditLogComponent({
	resourceType,
	resourceId,
	showSubResourcesId,
	limit = LIMIT_DEFAULT,
	relatedAccountId,
}: AuditLogEventsProps) {
	const infiniteScrollRef = useRef<HTMLSpanElement>(null)
	const accessToken = useAccessToken()
	const [relatedAccountAuditLogs, setRelatedAccountAuditLogs] = useState<AuditLog[]>([])

	useEffect(() => {
		if (relatedAccountId) {
			findAuditLogsByRelatedAccount(accessToken, 0, 1000, relatedAccountId, [
				"account-config-override",
				"account-hold",
			]).then((result) => {
				if (result.isOk()) {
					setRelatedAccountAuditLogs(result.value.data)
				}
			})
		}
	}, [relatedAccountId, accessToken])

	const defaultAuditLogApi = (offset: number, limit: number) =>
		findAuditLogs(accessToken, offset, limit, [resourceId], [resourceType])

	const auditLogApiCallback: Record<
		AuditLogResource,
		(offset: number, limit: number) => Promise<Result<OkDocument<AuditLog[]>, ErrorDocument>>
	> = {
		application: (offset, limit) => findApplicationAuditLogs(accessToken, offset, limit, resourceId),
		"deposit-product": defaultAuditLogApi,
		customer: (offset, limit) => findCustomerAuditLogs(accessToken, offset, limit, resourceId),
		account: defaultAuditLogApi,
		"account-hold": defaultAuditLogApi,
		"wallet-terms": defaultAuditLogApi,
		IncomingACH: defaultAuditLogApi,
		IncomingWire: defaultAuditLogApi,
		IncomingRealTimePayment: defaultAuditLogApi,
		WirePayment: defaultAuditLogApi,
		AchPayment: defaultAuditLogApi,
		FedWirePayment: defaultAuditLogApi,
		productApplication: (offset, limit) => findProductApplicationAuditLogs(accessToken, offset, limit, resourceId),
		card: defaultAuditLogApi,
		"account-config-override": defaultAuditLogApi,
	}

	const getAuditLogApi = () => {
		return auditLogApiCallback[resourceType] ?? defaultAuditLogApi
	}

	const [result, , hasNext, , next, , , isLoading] = useInfiniteScroll(
		limit,
		getAuditLogApi(),
		(x) => x.data.length,
		[],
		""
	)

	const shouldAnimateEvent = (originalIndex: number) => originalIndex >= limit

	return (
		<div className="audit-log-drawer-content">
			<AsyncResultComponent asyncResult={result} pendingComponent={<AuditLogPending />}>
				{({value: {data}}) => {
					const mergedData = [...data, ...relatedAccountAuditLogs].sort((a, b) =>
						b.attributes.actionTimestamp.localeCompare(a.attributes.actionTimestamp)
					)
					return (
						<>
							<AuditLogEventsView
								auditLogEventsByUserAndTimestamp={getProcessedAuditLogResults(
									mergedData,
									showSubResourcesId,
									resourceType,
									resourceId
								)}
								shouldAnimateEvent={shouldAnimateEvent}
							/>
							{hasNext && (
								<span ref={infiniteScrollRef}>
									<InfiniteScroll
										observerElementRef={infiniteScrollRef}
										callback={next}
										isLoading={isLoading}
										className="audit-log-content-loader"
									/>
								</span>
							)}
						</>
					)
				}}
			</AsyncResultComponent>
		</div>
	)
}
export default AuditLogComponent
